import { cn } from '@/lib/cn';

export interface SwitchProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
  size?: 'sm' | 'md';
  /** Accessible name when the switch has no flanking text labels. */
  ariaLabel?: string;
  /** Text shown on the "on" side of the toggle. */
  onLabel?: string;
  /** Text shown on the "off" side of the toggle. */
  offLabel?: string;
  className?: string;
}

const SIZES = {
  sm: { track: 'w-9 h-5', knob: 'w-4 h-4', on: 'translate-x-4' },
  md: { track: 'w-11 h-6', knob: 'w-5 h-5', on: 'translate-x-5' },
} as const;

/**
 * Unified on/off control for every boolean "enable" state across the admin.
 * Replaces the ad-hoc inline toggles / on-off <Select> dropdowns that looked
 * different per panel and were indistinguishable from plain form inputs.
 */
export function Switch({
  checked,
  onChange,
  disabled,
  size = 'md',
  ariaLabel,
  onLabel,
  offLabel,
  className,
}: SwitchProps) {
  const dims = SIZES[size];

  const toggle = (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={ariaLabel}
      disabled={disabled}
      onClick={() => !disabled && onChange(!checked)}
      className={cn(
        'relative rounded-full transition-colors flex-shrink-0',
        'focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1 focus:ring-offset-background',
        dims.track,
        checked ? 'bg-success' : 'bg-muted',
        disabled && 'opacity-50 cursor-not-allowed'
      )}
    >
      <span
        className={cn(
          'absolute top-0.5 left-0.5 bg-card rounded-full shadow transition-transform',
          dims.knob,
          checked ? dims.on : 'translate-x-0'
        )}
      />
    </button>
  );

  if (!onLabel && !offLabel) {
    return className ? <span className={className}>{toggle}</span> : toggle;
  }

  return (
    <span className={cn('inline-flex items-center gap-2', className)}>
      {offLabel && (
        <span className={cn('text-sm', checked ? 'text-muted-foreground' : 'font-medium text-foreground')}>
          {offLabel}
        </span>
      )}
      {toggle}
      {onLabel && (
        <span className={cn('text-sm', checked ? 'font-medium text-foreground' : 'text-muted-foreground')}>
          {onLabel}
        </span>
      )}
    </span>
  );
}

Switch.displayName = 'Switch';
